/**
 * What a partner does, shared across the boundary.
 *
 * This module used to describe a marketing application form on
 * tabbio.com/partners: its statuses, its acknowledgement email, its admin row
 * shape and two generations of legacy track discriminators. None of that
 * survives. The join form on partners.tabbio.com IS the signup now, so nothing
 * writes an application, nothing reads one, and the only thing that form asks
 * about a person is stored on their partner ACCOUNT.
 *
 * The `partner_application` TABLE still exists. It was deliberately not dropped:
 * it holds the record of everybody who joined before this change, and a
 * destructive migration bought for tidiness is a bad trade. It is simply no
 * longer part of any code path.
 *
 * The const array below is the single source of truth for the allowed values.
 * It is shared deliberately: apps/api is on zod ^3 and the web apps are on
 * zod ^4, so the schemas are duplicated on each side but both build their
 * `z.enum(...)` from this array, which makes the allowed set structurally
 * impossible to drift. `apps/partners` and `apps/admin` cannot import this
 * package at all and hand-mirror the values instead, each pinned by its own test.
 */

/**
 * What a partner does, from the one question the join form asks: "which best
 * describes your work?". Stored on `PartnerAccount.partnerRole`, and the axis
 * the admin console splits the programme by.
 *
 * Six options, so the brand's ban on rule-of-three lists is not in play and
 * `cv_writer` is back. It was dropped once purely to get an older two-value list
 * down from three to two; that reason is gone. Do not re-drop it.
 *
 * `ugc_creator` keeps a spelling inherited from that older list on purpose, so
 * rows written before this need no rewrite.
 */
export const PARTNER_ROLES = [
  'ugc_creator',
  'career_coach',
  'cv_writer',
  'recruiter',
  'agency',
  'other',
] as const;

export type PartnerRole = (typeof PARTNER_ROLES)[number];
